import { IcartInterface, Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../commerce-client';
import { Item } from './item-related/item.define';

/**
 * Represents cookie settings for various commerce features.
 * @interface Icookie
 * @property {boolean} cartEnabled - Whether cart functionality is enabled.
 * @property {boolean} recentEnabled - Whether recent items functionality is enabled.
 * @property {boolean} wishListEnabled - Whether wishlist functionality is enabled.
 * @property {boolean} compareListEnabled - Whether compare list functionality is enabled.
 */
export interface Icookie {
  cartEnabled: boolean;
  recentEnabled: boolean;
  wishListEnabled: boolean;
  compareListEnabled: boolean;
}

/**
 * Manages user-specific cookie settings and operations for commerce features.
 * Provides methods to interact with cart, recent items, wishlist, and compare list.
 *
 * @class Cookies
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Cookies} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const cookies = new Cookies();
 * await cookies.getSettings('user-123');
 * ```
 */
export class Cookies {
  /** Base URL for cookies API endpoints. @private @static @readonly */
  private static readonly baseUrl = '/cookies';

  /** Whether cart functionality is enabled. @default true */
  cartEnabled = true;

  /** Whether recent items functionality is enabled. @default true */
  recentEnabled = true;

  /** Whether wishlist functionality is enabled. @default true */
  wishListEnabled = true;

  /** Whether compare list functionality is enabled. @default true */
  compareListEnabled = true;

  /**
   * Creates a new Cookies instance with all features enabled by default.
   * @constructor
   */
  constructor() { }

  /**
   * Retrieves and applies cookie settings for a specific user.
   *
   * @param {string} [userId] - Optional user ID to retrieve settings for.
   * @returns {Promise<void>} Resolves when settings are retrieved and applied.
   * @throws {Error} When the API request fails.
   *
   * @example
   * const cookies = new Cookies();
   * await cookies.getSettings('user-123');
   */
  async getSettings(userId?: string): Promise<void> {
    const observer$ = CommerceClient.ehttp
      .makePost<Icookie>(`${Cookies.baseUrl}/getsettings`, { userId });
    const settings = await lastValueFrom(observer$);

    this.cartEnabled = settings?.cartEnabled;
    this.recentEnabled = settings?.recentEnabled;
    this.wishListEnabled = settings?.wishListEnabled;
    this.compareListEnabled = settings?.compareListEnabled;
  }

  /**
   * Updates cookie settings for a specific user.
   *
   * @param {Icookie} settings - New cookie settings to apply.
   * @param {string} [userId] - Optional user ID to update settings for.
   * @returns {Promise<Icookie>} The updated cookie settings.
   * @throws {Error} When the update operation fails.
   *
   * @example
   * await cookies.updateSettings({
   *   cartEnabled: true,
   *   recentEnabled: false,
   *   wishListEnabled: true,
   *   compareListEnabled: false
   }, 'user-123');
   */
  async updateSettings(settings: Icookie, userId?: string): Promise<Icookie> {
    const observer$ = CommerceClient.ehttp
      .makePost<Icookie>(`${Cookies.baseUrl}/update-settings`, { userId, settings });
    const response = await lastValueFrom(observer$);

    if (response.cartEnabled) {
      this.cartEnabled = settings.cartEnabled;
      this.recentEnabled = settings.recentEnabled;
      this.wishListEnabled = settings.wishListEnabled;
      this.compareListEnabled = settings.compareListEnabled;
    }

    return response;
  }

  /**
   * Adds an item to the cart if cart functionality is enabled.
   *
   * @param {string} cartItemId - The ID of the item to add to cart.
   * @param {number} totalCostwithNoShipping - The item's total cost excluding shipping.
   * @param {string} [userId] - Optional user ID associated with the cart.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  addCartItem(
    cartItemId: string,
    totalCostwithNoShipping: number,
    userId?: string
  ): Promise<Isuccess> {
    if (!this.cartEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/add-cart-item`, { userId, cartItemId, totalCostwithNoShipping });

    return lastValueFrom(observer$);
  }

  /**
   * Adds an item to the recent items list if the feature is enabled.
   *
   * @param {string} recentItemId - The ID of the item to mark as recently viewed.
   * @param {string} [userId] - Optional user ID.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  addRecent(
    recentItemId: string,
    userId?: string
  ): Promise<Isuccess> {
    if (!this.recentEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/add-recent`, { userId, recentItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Adds an item to the wishlist if the feature is enabled.
   *
   * @param {string} wishListItemId - The ID of the item to add to wishlist.
   * @param {string} [userId] - Optional user ID.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  addWishList(
    wishListItemId: string,
    userId?: string
  ): Promise<Isuccess> {
    if (!this.wishListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/add-wish-list`, { userId, wishListItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Adds an item to the compare list if the feature is enabled.
   *
   * @param {string} compareListItemId - The ID of the item to add for comparison.
   * @param {string} [userId] - Optional user ID.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  addCompareList(
    compareListItemId: string,
    userId?: string
  ): Promise<Isuccess> {
    if (!this.compareListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/add-compare-list-items`, { userId, compareListItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Removes an item from the cart if cart functionality is enabled.
   *
   * @param {string} cartItemId - The ID of the item to remove from cart.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  deleteCartItem(cartItemId: string): Promise<Isuccess> {
    if (!this.cartEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/delete-cart-item`, { cartItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Removes an item from the wishlist if the feature is enabled.
   *
   * @param {string} wishListItemId - The ID of the item to remove from wishlist.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  deleteWishListItem(wishListItemId: string): Promise<Isuccess> {
    if (!this.wishListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/delete-wishlist-item`, { wishListItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Removes an item from the compare list if the feature is enabled.
   *
   * @param {string} compareListItemId - The ID of the item to remove from comparison.
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  deleteCompareListItem(compareListItemId: string): Promise<Isuccess> {
    if (!this.compareListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/delete-compare-list-item`, { compareListItemId });

    return lastValueFrom(observer$);
  }

  /**
   * Clears all items from the cart if cart functionality is enabled.
   *
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  clearCart(): Promise<Isuccess> {
    if (!this.cartEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/clear-cart`, {});

    return lastValueFrom(observer$);
  }

  /**
   * Clears all items from the wishlist if the feature is enabled.
   *
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  clearWishList(): Promise<Isuccess> {
    if (!this.wishListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/clear-wishlist`, {});

    return lastValueFrom(observer$);
  }

  /**
   * Clears all items from the compare list if the feature is enabled.
   *
   * @returns {Promise<Isuccess>} Operation success status.
   * @throws {Error} When the operation fails.
   */
  clearCompareList(): Promise<Isuccess> {
    if (!this.compareListEnabled) {
      return Promise.resolve({ success: true });
    }
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Cookies.baseUrl}/clear-compare-list`, {});

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all items from the cart if cart functionality is enabled.
   *
   * @returns {Promise<IcartInterface[]>} Array of cart items.
   * @throws {Error} When the operation fails.
   */
  getFromCart(): Promise<IcartInterface[]> {
    if (!this.cartEnabled) {
      return Promise.resolve([]);
    }
    const observer$ = CommerceClient.ehttp
      .makeGet<IcartInterface[]>(`${Cookies.baseUrl}/get-from-cart`);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves recently viewed items if the feature is enabled.
   *
   * @returns {Promise<Item[]>} Array of recently viewed items.
   * @throws {Error} When the operation fails.
   */
  getFromRecent(): Promise<Item[]> {
    if (!this.recentEnabled) {
      return Promise.resolve([]);
    }
    const observer$ = CommerceClient.ehttp
      .makeGet<Item[]>(`${Cookies.baseUrl}/get-from-recent`);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves wishlist items if the feature is enabled.
   *
   * @returns {Promise<Item[]>} Array of wishlist items.
   * @throws {Error} When the operation fails.
   */
  getFromWishList(): Promise<Item[]> {
    if (!this.wishListEnabled) {
      return Promise.resolve([]);
    }
    const observer$ = CommerceClient.ehttp
      .makeGet<Item[]>(`${Cookies.baseUrl}/get-from-wishlist`);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves items in the compare list if the feature is enabled.
   *
   * @returns {Promise<Item[]>} Array of items for comparison.
   * @throws {Error} When the operation fails.
   */
  getFromCompareList(): Promise<Item[]> {
    if (!this.compareListEnabled) {
      return Promise.resolve([]);
    }
    const observer$ = CommerceClient.ehttp
      .makeGet<Item[]>(`${Cookies.baseUrl}/get-from-compare-list`);

    return lastValueFrom(observer$);
  }
}
